import { createContext, useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import authService from 'src/services/api/auth';
import {
  fetchBulletins as fetchBulletinsAPI,
  saveEntry as saveEntryAPI,
  markBulletinAsRead as markBulletinAsReadAPI,
  fetchInteractions as fetchInteractionsAPI,
  fetchAllUsers as fetchAllUsersAPI,
} from '../../services/trainingViewService';
import { isEntryReadByUser } from '../../utils/tableroGestionUtils';
import { chPerm } from 'src/components/helpers';

const TrainingContext = createContext(null);

export const TrainingContextProvider = ({ children }) => {
  // State management
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const [interactions, setInteractions] = useState([]);
  const [loadingInteractions, setLoadingInteractions] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const currentUser = authService.getCurrentUser();
  const currentUserId = currentUser?.id;

  // Fetch bulletins from API
  const fetchBulletins = useCallback(async () => {
    setLoading(true);
    try {
      const bulletins = await fetchBulletinsAPI();
      setEntries(bulletins);
    } catch (error) {
      console.error('Error getting bulletins:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'No se pudieron cargar las entradas. Por favor, intenta de nuevo.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch interactions from API
  const fetchInteractions = useCallback(async (type = null) => {
    setLoadingInteractions(true);
    try {
      const interactionsData = await fetchInteractionsAPI(type);
      setInteractions(interactionsData);
    } catch (error) {
      console.error('Error getting interactions:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text:
          error.response?.data?.message ||
          error.message ||
          'No se pudieron cargar las interacciones. Por favor, intenta de nuevo.',
      });
    } finally {
      setLoadingInteractions(false);
    }
  }, []);

  // Fetch all users from API
  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const usersData = await fetchAllUsersAPI();
      setUsers(usersData);
    } catch (error) {
      console.error('Error getting users:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text:
          error.response?.data?.message ||
          error.message ||
          'No se pudieron cargar los usuarios. Por favor, intenta de nuevo.',
      });
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  // Fetch all data on mount
  useEffect(() => {
    fetchBulletins();
    fetchInteractions();
    if (chPerm(['intranetAdmin'], true)) {
      fetchUsers();
    }
  }, [fetchBulletins, fetchInteractions, fetchUsers]);

  // Sync selectedEntry when entries are updated (e.g., after marking as read)
  useEffect(() => {
    if (selectedEntry) {
      const updatedEntry = entries.find((e) => e.id === selectedEntry.id);
      if (updatedEntry && JSON.stringify(updatedEntry) !== JSON.stringify(selectedEntry)) {
        setSelectedEntry(updatedEntry);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries]);

  const handleEntrySelect = (entry) => {
    setSelectedEntry(entry);
  };

  const handleBack = () => {
    setSelectedEntry(null);
  };

  const handleCreateEntry = () => {
    setEditingEntry(null);
    setModalOpen(true);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setModalOpen(true);
  };

  const handleSaveEntry = async (entryData) => {
    try {
      const response = await saveEntryAPI(entryData);

      Swal.fire({
        icon: 'success',
        title: entryData.id ? 'Entrada actualizada' : 'Entrada creada',
        text: response.message,
        timer: 2000,
        showConfirmButton: false,
      });

      // Refresh the entries list to get updated data
      fetchBulletins();

      // Update selected entry if it's the one being edited
      if (entryData.id && selectedEntry?.id === entryData.id) {
        setTimeout(() => {
          const updatedEntry = response.bulletin;
          if (updatedEntry) {
            setSelectedEntry(updatedEntry);
          }
        }, 100);
      } else if (!entryData.id && response.bulletin) {
        // Select the newly created entry
        setSelectedEntry(response.bulletin);
      }
    } catch (error) {
      console.error('Error saving entry:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text:
          error.response?.data?.message ||
          error.message ||
          'No se pudo guardar la entrada. Por favor, intenta de nuevo.',
      });
      throw error; // Re-throw to prevent modal from closing
    }
  };

  const handleMarkAsRead = async (entry) => {
    if (!entry || !currentUserId || isEntryReadByUser(entry, currentUserId)) return;

    setMarkingAsRead(true);
    try {
      await markBulletinAsReadAPI(entry.id, currentUserId);

      // Update the entry in the entries list
      setEntries((prevEntries) =>
        prevEntries.map((e) => {
          if (e.id === entry.id) {
            const updatedReadedBy = Array.isArray(e.readed_by) ? [...e.readed_by] : [];
            if (!updatedReadedBy.includes(currentUserId)) {
              updatedReadedBy.push(currentUserId);
            }
            return { ...e, readed_by: updatedReadedBy };
          }
          return e;
        })
      );

      // Update selected entry if it's the one being marked
      if (selectedEntry?.id === entry.id) {
        const updatedReadedBy = Array.isArray(selectedEntry.readed_by)
          ? [...selectedEntry.readed_by]
          : [];
        if (!updatedReadedBy.includes(currentUserId)) {
          updatedReadedBy.push(currentUserId);
        }
        setSelectedEntry({ ...selectedEntry, readed_by: updatedReadedBy });
      }

      Swal.fire({
        icon: 'success',
        title: 'Marcado como leído',
        text: 'La entrada ha sido marcada como leída.',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Error marking as read:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text:
          error.response?.data?.message ||
          error.message ||
          'No se pudo marcar como leído. Por favor, intenta de nuevo.',
      });
    } finally {
      setMarkingAsRead(false);
    }
  };

  const handleMarkAsReadAndUpdate = async (entry) => {
    await handleMarkAsRead(entry);
  };

  const value = {
    // State
    entries,
    loading,
    modalOpen,
    editingEntry,
    markingAsRead,
    interactions,
    loadingInteractions,
    users,
    loadingUsers,
    selectedEntry,
    currentUserId,
    // Setters
    setModalOpen,
    setSelectedEntry,
    // Functions
    fetchBulletins,
    fetchInteractions,
    fetchUsers,
    handleEntrySelect,
    handleBack,
    handleCreateEntry,
    handleEditEntry,
    handleSaveEntry,
    handleMarkAsRead,
    handleMarkAsReadAndUpdate,
  };

  return <TrainingContext.Provider value={value}>{children}</TrainingContext.Provider>;
};

export default TrainingContext;
